import { useState, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./customer-transaction-history-detail.module.scss";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { CustomerLoader } from "../../components";
import {
	useGetPrepaidTransactionByReferenceForCustomerQuery,
	useGetPostpaidTransactionByReferenceForCustomerQuery,
	useGetOrderTransactionByReferenceForCustomerQuery,
	useGetInstallmentTransactionByReferenceForCustomerQuery,
	useGetPayAsYouGoTransactionByReferenceForCustomerQuery,
	useGetBulkPurchaseTransactionByReferenceForCustomerQuery,
	useGetServiceRequestTransactionByReferenceForCustomerQuery,
	useLazyDownloadTransactionInvoiceQuery,
} from "../../../../service/transaction-service";
import { Transaction, TransactionCategory } from "../../../../model/transaction";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils/error-handler";
import {
	VendingTransactionDetail,
	BillingTransactionDetail,
	OrderTransactionDetail,
	InstallmentTransactionDetail,
	PayAsYouGoTransactionDetail,
	BulkPurchaseTransactionDetail,
	ServiceRequestTransactionDetail,
	FriendPaymentRequestDetailsPage,
	TransactionReceiptHeader,
	ShareReceiptModal,
} from "./components";
import { TopBottomCuttedCard } from "../../../../common/components/top-bottom-cutted-card/TopBottomCuttedCard";
import {
	ShareIcon,
	VendingTransactionHistory,
	BillingTransactionHistory,
	OrderTransactionHistory,
	InstallmentTransactionHistory,
	PayAsYouGoTransactionHistory,
	BulkTransactionHistory,
	ServiceRequestTransactionHistory,
} from "../../../../assets/icons";
import bgTransaction from "../../../../assets/background/bg-transaction.png";
import { useTenant } from "../../../../tenant/branding/useTenant";
import { shareReceiptAsImage } from "../../../../common/utils/receipt/shareReceiptAsImage";
import { shareReceiptAsPdf } from "../../../../common/utils/receipt/shareReceiptAsPdf";
import Button from "../../../../common/ekedc-components/button/Button";

export const CustomerTransactionHistoryDetailsPage = () => {
	const { t } = useTranslation();
	const { logoWithText } = useTenant();
	const { reference } = useParams<{ reference: string }>();
	const navigate = useNavigate();
	const { state } = useLocation();
	const [searchParams] = useSearchParams();

	const category: number | undefined = state?.category ?? (searchParams.get("category") !== null ? Number(searchParams.get("category")) : undefined);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const preloadedTransaction = state?.transaction as any;

	const [showTokenView, setShowTokenView] = useState(false);
	const [showShareModal, setShowShareModal] = useState(false);
	const receiptRef = useRef<HTMLDivElement>(null);
	const modalReceiptRef = useRef<HTMLDivElement>(null);

	const ref = reference || "";

	const [downloadInvoice, { isLoading: downLoadingInvoice }] = useLazyDownloadTransactionInvoiceQuery();

	const {
		data: prepaidTransaction,
		isLoading: prepaidLoading,
		error: prepaidError,
	} = useGetPrepaidTransactionByReferenceForCustomerQuery(
		{ Reference: ref },
		{ skip: !!preloadedTransaction || !ref || category !== TransactionCategory.VendingTransaction }
	);
	const {
		data: postpaidTransaction,
		isLoading: postpaidLoading,
		error: postpaidError,
	} = useGetPostpaidTransactionByReferenceForCustomerQuery(
		{ Reference: ref },
		{ skip: !!preloadedTransaction || !ref || category !== TransactionCategory.BillingTransaction }
	);
	const {
		data: orderTransaction,
		isLoading: orderLoading,
		error: orderError,
	} = useGetOrderTransactionByReferenceForCustomerQuery(ref, { skip: !!preloadedTransaction || !ref || category !== TransactionCategory.OrderTransactions });
	const {
		data: installmentTransaction,
		isLoading: installmentLoading,
		error: installmentError,
	} = useGetInstallmentTransactionByReferenceForCustomerQuery(ref, {
		skip: !!preloadedTransaction || !ref || category !== TransactionCategory.InstallmentTransaction,
	});
	const {
		data: payAsYouGoTransaction,
		isLoading: payAsYouGoLoading,
		error: payAsYouGoError,
	} = useGetPayAsYouGoTransactionByReferenceForCustomerQuery(
		{ Reference: ref },
		{ skip: !!preloadedTransaction || !ref || category !== TransactionCategory.PayAsYouGoTransaction }
	);
	const {
		data: bulkTransaction,
		isLoading: bulkLoading,
		error: bulkError,
	} = useGetBulkPurchaseTransactionByReferenceForCustomerQuery(ref, {
		skip: !!preloadedTransaction || !ref || category !== TransactionCategory.BulkPurchaseTransaction,
	});
	const {
		data: serviceRequestTransaction,
		isLoading: serviceRequestLoading,
		error: serviceRequestError,
	} = useGetServiceRequestTransactionByReferenceForCustomerQuery(ref, {
		skip: !!preloadedTransaction || !ref || category !== TransactionCategory.ServiceRequesTransaction,
	});

	const fetchedTransaction =
		category === TransactionCategory.BillingTransaction
			? postpaidTransaction
			: category === TransactionCategory.OrderTransactions
				? orderTransaction
				: category === TransactionCategory.InstallmentTransaction
					? installmentTransaction
					: category === TransactionCategory.PayAsYouGoTransaction
						? payAsYouGoTransaction
						: category === TransactionCategory.BulkPurchaseTransaction
							? bulkTransaction
							: category === TransactionCategory.ServiceRequesTransaction
								? serviceRequestTransaction
								: prepaidTransaction;

	const transaction = preloadedTransaction ?? fetchedTransaction;

	const isLoading =
		!preloadedTransaction &&
		(prepaidLoading || postpaidLoading || orderLoading || installmentLoading || payAsYouGoLoading || bulkLoading || serviceRequestLoading);

	const error = preloadedTransaction
		? undefined
		: category === TransactionCategory.BillingTransaction
			? postpaidError
			: category === TransactionCategory.OrderTransactions
				? orderError
				: category === TransactionCategory.InstallmentTransaction
					? installmentError
					: category === TransactionCategory.PayAsYouGoTransaction
						? payAsYouGoError
						: category === TransactionCategory.BulkPurchaseTransaction
							? bulkError
							: category === TransactionCategory.ServiceRequesTransaction
								? serviceRequestError
								: prepaidError;

	const handleBack = () => {
		if (category === TransactionCategory.VendingTransaction && showTokenView) {
			setShowTokenView(false);
		} else {
			navigate(-1);
		}
	};

	const handleDownloadReceipt = () => {
		downloadInvoice(reference ?? "")
			.unwrap()
			.then(res => {
				if (res) {
					const link = document.createElement("a");
					link.href = res;
					const urlParts = res.split("/");
					const filename = urlParts.at(-1) || `transaction-${reference}.pdf`;
					link.download = filename;
					link.target = "_blank";
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					toast.success(t("history.web.receiptDownloadedSuccessfully"));
				}
			})
			.catch(err => {
				toast.error(getErrorMessage(err?.data));
			});
	};

	const handleSharePdf = () => {
		if (!modalReceiptRef.current) return;
		shareReceiptAsPdf(modalReceiptRef.current, reference);
	};

	const handleShareImage = () => {
		if (!modalReceiptRef.current) return;
		shareReceiptAsImage(modalReceiptRef.current, reference);
	};

	const isTokenView = category === TransactionCategory.VendingTransaction && showTokenView;
	const title = isTokenView ? t("history.web.tokenDetails") : t("common.labels.historyDetails");
	const layoutClass = isTokenView ? styles.noPadding : undefined;

	const categoryIcon = (() => {
		switch (category) {
			case TransactionCategory.VendingTransaction:
				return <VendingTransactionHistory />;
			case TransactionCategory.BillingTransaction:
				return <BillingTransactionHistory />;
			case TransactionCategory.OrderTransactions:
				return <OrderTransactionHistory />;
			case TransactionCategory.InstallmentTransaction:
				return <InstallmentTransactionHistory />;
			case TransactionCategory.PayAsYouGoTransaction:
				return <PayAsYouGoTransactionHistory />;
			case TransactionCategory.BulkPurchaseTransaction:
				return <BulkTransactionHistory />;
			case TransactionCategory.ServiceRequesTransaction:
				return <ServiceRequestTransactionHistory />;
			default:
				return undefined;
		}
	})();

	if (isLoading) {
		return (
			<CustomerPageLayout title={title} onBack={handleBack}>
				<div className={styles.pageContainer}>
					<CustomerLoader subtitle={t("history.web.loadingTransactionDetails")} />
				</div>
			</CustomerPageLayout>
		);
	}

	if (error || !transaction) {
		return (
			<CustomerPageLayout title={title} onBack={handleBack}>
				<div className={styles.pageContainer}>
					<div className={styles.errorState}>
						<p>{t("history.web.failedToLoadTransactionDetails")}</p>
						<button className={styles.retryButton} onClick={() => navigate(-1)}>
							{t("history.web.goBack")}
						</button>
					</div>
				</div>
			</CustomerPageLayout>
		);
	}

	// Ask-a-Friend-to-Pay requests get their own dedicated page (matches Flutter), not the
	// generic receipt-style wrapper below.
	if (transaction.transactionPaymentType === 6) {
		return <FriendPaymentRequestDetailsPage transaction={transaction} onBack={handleBack} />;
	}

	const renderDetail = (shareView: boolean) => {
		switch (category) {
			case TransactionCategory.BillingTransaction:
				return (
					<BillingTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			case TransactionCategory.OrderTransactions:
				return (
					<OrderTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			case TransactionCategory.InstallmentTransaction:
				return (
					<InstallmentTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			case TransactionCategory.PayAsYouGoTransaction:
				return (
					<PayAsYouGoTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			case TransactionCategory.BulkPurchaseTransaction:
				return (
					<BulkPurchaseTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			case TransactionCategory.ServiceRequesTransaction:
				return (
					<ServiceRequestTransactionDetail
						transaction={transaction}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
			default:
				return (
					<VendingTransactionDetail
						transaction={transaction}
						showTokenView={showTokenView}
						onHideTokenView={() => setShowTokenView(false)}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloading={downLoadingInvoice}
						shareView={shareView}
					/>
				);
		}
	};

	return (
		<CustomerPageLayout title={title} onBack={handleBack} contentContainerClass={styles.contentDetails}>
			<div className={styles.pageContainer}>
				<TopBottomCuttedCard ref={receiptRef}>
					<TransactionReceiptHeader
						status={transaction.status}
						amount={
							category === 3 ? ((transaction as Transaction).orderDetails?.order?.costOverview?.totalAmount.amount ?? 0) : (transaction.amount?.amount ?? 0)
						}
						currency={category === 3 ? (transaction as Transaction).orderDetails?.order?.costOverview?.totalAmount.currency : transaction.amount?.currency}
						createdAt={transaction.createdAt}
						shareView={false}
						icon={categoryIcon}
					/>
					{renderDetail(false)}
				</TopBottomCuttedCard>

				<Button variant="primary" className={styles.shareBtn} onClick={() => setShowShareModal(true)}>
					<ShareIcon /> {t("history.shareReceipt")}
				</Button>
			</div>

			{showShareModal && (
				<ShareReceiptModal
					ref={modalReceiptRef}
					onClose={() => setShowShareModal(false)}
					onShareImage={handleShareImage}
					onSharePdf={handleSharePdf}
					bgImage={bgTransaction}
					watermarkSrc={typeof logoWithText === "string" ? logoWithText : undefined}
					receiptStyle={{ "--receipt-circle-color": "var(--customer-bg)" } as React.CSSProperties}
				>
					<TransactionReceiptHeader
						status={transaction.status}
						amount={
							category == 3 ? ((transaction as Transaction).orderDetails?.order?.costOverview?.totalAmount.amount ?? 0) : (transaction.amount?.amount ?? 0)
						}
						currency={category == 3 ? (transaction as Transaction).orderDetails?.order?.costOverview?.totalAmount.currency : transaction.amount?.currency}
						createdAt={transaction.createdAt}
						shareView={true}
						icon={categoryIcon}
					/>
					{renderDetail(true)}
				</ShareReceiptModal>
			)}
		</CustomerPageLayout>
	);
};
